import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { alpha } from "@mui/material/styles";
import colors from "../../../assets/theme/colors";
import { removeProfileGoal, updateProfileGoal } from "../../../services/profileGoalService";
import LogFormShell from "../../UI/LogFormShell";

const normalizeText = (value = "") => String(value || "").trim();

const getGoalStatusLabel = (status = "active") => (
  normalizeText(status || "active") === "completed"
    ? "Completed"
    : normalizeText(status || "active") === "active" ? "Active" : "Paused"
);

const actionButtonSx = {
  minHeight: 34,
  px: 0,
  textTransform: "none",
  fontSize: "0.9rem",
  fontWeight: 900,
  color: colors.semantic.sageDeep,
};

const ProfileGoalSheet = ({
  open,
  goal,
  child,
  onClose,
  onAddEvidence,
  onChanged,
  onRemoved,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState("");
  const [draftDescription, setDraftDescription] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setError("");
      setSaving(false);
      setConfirmingRemove(false);
    }
    setDraftTitle(goal?.title || "");
    setDraftDescription(goal?.description || "");
  }, [goal, open]);

  const supportProfile = child?.supportProfile || {};
  const goalId = goal?.id || goal?.source?.id || goal?.goalId || "";
  const status = goal?.status || "active";
  const isActive = status === "active";

  const saveGoalUpdates = async (updates = {}) => {
    setSaving(true);
    setError("");
    try {
      const nextSupportProfile = await updateProfileGoal({
        childId: child?.id,
        childName: child?.name,
        supportProfile,
        goalId,
        updates,
      });
      onChanged?.(nextSupportProfile);
      return nextSupportProfile;
    } catch (saveError) {
      setError(saveError?.message || "Couldn't update this goal.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const cleanTitle = normalizeText(draftTitle);
    if (!cleanTitle) {
      setError("Add a goal before saving.");
      return;
    }

    const result = await saveGoalUpdates({
      title: cleanTitle,
      description: normalizeText(draftDescription),
    });
    if (result) {
      setIsEditing(false);
    }
  };

  const handleToggleStatus = async () => {
    await saveGoalUpdates({ status: isActive ? "paused" : "active" });
  };

  const handleComplete = async () => {
    const result = await saveGoalUpdates({ status: "completed" });
    if (result) onClose?.();
  };

  const handleDelete = async () => {
    setSaving(true);
    setError("");
    try {
      const nextSupportProfile = await removeProfileGoal({
        childId: child?.id,
        childName: child?.name,
        supportProfile,
        goalId,
      });
      onChanged?.(nextSupportProfile);
      onRemoved?.({ goal, nextSupportProfile });
      onClose?.();
    } catch (deleteError) {
      setError(deleteError?.message || "Couldn't delete this goal.");
    } finally {
      setSaving(false);
    }
  };

  const footer = confirmingRemove ? (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
      <Typography sx={{ fontWeight: 850, color: colors.semantic.ink }}>
        Remove this goal from the Profile?
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button disabled={saving} onClick={() => setConfirmingRemove(false)} sx={actionButtonSx}>
          Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleDelete}
          sx={{ ...actionButtonSx, color: colors.semantic.error || "#B91C1C" }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Remove"}
        </Button>
      </Stack>
    </Stack>
  ) : (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
      <Button
        type="button"
        variant="text"
        disabled={saving}
        onClick={() => setConfirmingRemove(true)}
        startIcon={<DeleteOutlineIcon fontSize="small" />}
        sx={{ ...actionButtonSx, color: colors.semantic.error || "#B91C1C" }}
      >
        Remove
      </Button>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          type="button"
          variant="text"
          disabled={saving}
          onClick={onClose}
          sx={{ ...actionButtonSx, color: colors.semantic.muted }}
        >
          Close
        </Button>
        {isEditing ? (
          <Button
            type="button"
            variant="contained"
            disabled={saving}
            onClick={handleSaveEdit}
            sx={{
              minHeight: 40,
              px: 2,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 950,
              bgcolor: colors.semantic.sageDeep,
              "&:hover": { bgcolor: colors.semantic.sageDeep },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );

  return (
    <LogFormShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Goal"
      subtitle={goal?.title || "Profile goal"}
      maxWidth="sm"
      footer={footer}
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            p: 1.3,
            borderRadius: "16px",
            border: `1px solid ${alpha(colors.semantic.line || "#CBD5E1", 0.78)}`,
            bgcolor: alpha(colors.semantic.sageDeep, 0.06),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 950, letterSpacing: "0.12em", textTransform: "uppercase", color: alpha(colors.semantic.ink, 0.58) }}>
                {getGoalStatusLabel(status)}
              </Typography>
              {isEditing ? (
                <Stack spacing={1.1} sx={{ mt: 1 }}>
                  <TextField
                    label="Goal"
                    fullWidth
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    disabled={saving}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={3}
                    value={draftDescription}
                    onChange={(event) => setDraftDescription(event.target.value)}
                    disabled={saving}
                  />
                </Stack>
              ) : (
                <>
                  <Typography sx={{ mt: 0.45, fontSize: "1.12rem", fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.16 }}>
                    {goal?.title || "Goal"}
                  </Typography>
                  {goal?.description ? (
                    <Typography sx={{ mt: 0.7, fontSize: "0.95rem", color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.4 }}>
                      {goal.description}
                    </Typography>
                  ) : null}
                </>
              )}
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Button
            type="button"
            variant="text"
            disabled={saving}
            onClick={() => setIsEditing((current) => !current)}
            startIcon={<EditOutlinedIcon fontSize="small" />}
            sx={actionButtonSx}
          >
            {isEditing ? "Cancel edit" : "Edit"}
          </Button>
          <Button
            type="button"
            variant="text"
            disabled={saving}
            onClick={handleToggleStatus}
            startIcon={isActive ? <StopRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
            sx={actionButtonSx}
          >
            {isActive ? "Pause" : "Resume"}
          </Button>
          <Button
            type="button"
            variant="text"
            disabled={saving}
            onClick={handleComplete}
            startIcon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
            sx={actionButtonSx}
          >
            Complete
          </Button>
          <Button
            type="button"
            variant="text"
            disabled={saving}
            onClick={() => onAddEvidence?.()}
            sx={actionButtonSx}
          >
            Add evidence
          </Button>
        </Stack>

        {error ? (
          <Alert severity="error">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </LogFormShell>
  );
};

export default ProfileGoalSheet;
